/**
 * Studies report: how many exams each center holds, by type, by patient and
 * over time — for one center or the whole fleet, ready to print.
 *
 * Counted from the per-study catalogue the upload rollup keeps of each
 * center's Orthanc index (fleet_control migration 011), not from the index at
 * request time, so the page is fast and every number comes from one snapshot.
 *
 * What the words on this page mean (the same definitions the backend's module
 * docstrings carry):
 *
 * * **Study** — one exam, counted once. Attached objects (a presentation
 *   state, a structured report, a key-object note) are part of the exam they
 *   annotate, not studies of their own; a study holding nothing else is
 *   labelled by them, and the footnotes under "Studies by type" say how many.
 * * **Exam date** — the study's StudyDate. A study without a valid one counts
 *   on the day it first arrived (and the info line says how many did).
 *   **Received date** — the day its first instance arrived. The two differ by
 *   the priors and archive imports the lag table shows.
 * * **Patients** — distinct patients per center. One person seen at two
 *   centers counts twice in a fleet total; the keys are per center by design.
 * * **Growth** — the same window before this one, today left out of both
 *   (it is still in progress), deleted studies counted in both.
 *
 * Three things this page is careful about:
 *
 * * **Not computed is not zero.** A center the catalogue has not finished
 *   reading is named in a callout, every unknown figure renders as a dash and
 *   exports as an empty cell, and each CSV carries its `provisional` marker.
 * * **Privacy.** Patient names and IDs are never read: patients are counted by
 *   a keyed pseudonym. Study descriptions are free text from the index, so the
 *   server sends them to operators and owners only.
 * * **The filters live in the URL,** so a center's report (`?slug=`) can be
 *   linked from its page, pasted to a colleague, or printed to PDF.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Badge } from '../components/Badge';
import type { Tone } from '../components/Badge';
import { Callout } from '../components/Callout';
import { PageHeader } from '../components/PageHeader';
import { ReportTabs } from '../components/ReportTabs';
import { bucketLabeller, percent } from '../components/reports/edgeReportFormat';
import {
  attachedCsv,
  attachedFootnote,
  basisNoun,
  bucketCount,
  byTypeCsv,
  catalogueCsv,
  centersCsv,
  changeCell,
  descriptionsCsv,
  growthHint,
  lagCsv,
  MAX_BUCKETS,
  modalityDisplay,
  modalityShort,
  otherBasis,
  patientsCsv,
  perBucketCsv,
  studyCsvName,
  studyReportPath,
  totalsCsv,
} from '../components/reports/studyReportFormat';
import type { CsvTable } from '../components/reports/studyReportFormat';
import { SortHeader, TableScroll, useSort, useSortedRows } from '../components/SortableTable';
import { StatGrid, StatTile } from '../components/StatTile';
import { ErrorBlock, LoadingBlock, StaleBanner } from '../components/States';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { SegmentedFilter } from '../components/ui/FilterBar';
import { Select, SelectItem, TextInput } from '../components/ui/Form';
import { DownloadIcon, RenewIcon } from '../components/ui/Icon';
import { ChartFrame, ColumnChart, RankedBars } from '../components/viz/Chart';
import type { Series } from '../components/viz/Chart';
import { ApiError, apiPost, describeError } from '../lib/api';
import { baghdadToday, calendarRange, daysInclusive, EXAM_DATE_MIN, isDay } from '../lib/baghdad';
import type { CalendarPreset } from '../lib/baghdad';
import { downloadCsv } from '../lib/csv';
import {
  formatBytes,
  formatDateTime,
  formatNumber,
  formatRelative,
  pluralise,
  UNKNOWN,
} from '../lib/format';
import { useResource } from '../lib/useResource';
import type {
  CenterListResponse,
  MeResponse,
  RollupRefreshResponse,
  RollupStatusResponse,
  StudyBasis,
  StudyCenterRow,
  StudyCompare,
  StudyGrowth,
  StudyReportResponse,
  UploadBucket,
} from '../types/api';

import '../styles/page-studies-report.css';

// ── filters ──────────────────────────────────────────────────────────────────

type PresetKey = CalendarPreset | 'custom';

/** Calendar windows, each with the bucket that suits it. Every one but
 *  "All time" and a custom range starts on the 1st, so it is compared with
 *  the same calendar stretch before it. */
const PRESETS: readonly { key: PresetKey; label: string; bucket: UploadBucket }[] = [
  { key: 'month', label: 'This month', bucket: 'day' },
  { key: '3m', label: 'Last 3 months', bucket: 'month' },
  { key: 'year', label: 'This year', bucket: 'month' },
  { key: 'all', label: 'All time', bucket: 'month' },
  { key: 'custom', label: 'Custom range…', bucket: 'month' },
];
const DEFAULT_PRESET: PresetKey = '3m';

const BUCKETS: readonly { key: UploadBucket; label: string; noun: string }[] = [
  { key: 'day', label: 'Daily', noun: 'day' },
  { key: 'week', label: 'Weekly (from Monday)', noun: 'week' },
  { key: 'month', label: 'Monthly', noun: 'month' },
];

const BASES: readonly { value: StudyBasis; label: string }[] = [
  { value: 'exam', label: 'Exam date' },
  { value: 'received', label: 'Received date' },
];

/** Labels that keep their own colour in the chart; the rest fold into
 *  "Other" (slot 8), the uploads page's rule. */
const TOP_MODALITIES = 7;

type UrlKey = 'range' | 'from' | 'to' | 'slug' | 'basis' | 'bucket';

function readPreset(value: string | null): PresetKey {
  return PRESETS.some((p) => p.key === value) ? (value as PresetKey) : DEFAULT_PRESET;
}
function readBucket(value: string | null): UploadBucket | null {
  return BUCKETS.some((b) => b.key === value) ? (value as UploadBucket) : null;
}
function readBasis(value: string | null): StudyBasis {
  return value === 'received' ? 'received' : 'exam';
}

/** The calendar presets compare like with like (Sep 1–16 against Oct 1–16);
 *  "All time" and a custom range compare the same number of days before. */
function compareFor(preset: PresetKey): StudyCompare {
  return preset === 'month' || preset === '3m' || preset === 'year' ? 'calendar' : 'length';
}

/** A custom range's natural bucket, the same steps the uploads page takes. */
function bucketForSpan(days: number): UploadBucket {
  if (days <= 62) return 'day';
  if (days <= 190) return 'week';
  return 'month';
}

/** An empty list's text when nothing is computed: null is not zero. */
const NOT_COMPUTED = 'Not computed yet — see the note above.';

/** The bucket-cap 400: its message is shown and the previous bucket kept. */
function isBucketRefusal(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 400 && /coarser bucket/i.test(error.detail);
}

// ── the chart's series ───────────────────────────────────────────────────────

/**
 * Buckets → one Series per exam label, the first `TOP_MODALITIES` of
 * `modalities` (largest first) keeping fixed slots and the rest summed into
 * "Other". A label absent from a bucket's `by_modality` is a counted zero —
 * the server only sends buckets once something is computed.
 */
function buildSeries(data: StudyReportResponse | null): Series[] {
  if (!data || data.buckets.length === 0) return [];
  const labels = data.modalities;
  const kept = labels.slice(0, labels.length > TOP_MODALITIES + 1 ? TOP_MODALITIES : TOP_MODALITIES + 1);
  const folded = labels.slice(kept.length);
  const series: Series[] = kept.map((code, index) => ({
    key: code || '__none__',
    label: modalityShort(code),
    slot: index + 1,
    points: data.buckets.map((b) => ({ at: b.bucket, value: b.by_modality[code] ?? 0 })),
  }));
  if (folded.length > 0) {
    series.push({
      key: '__other__',
      label: `Other (${formatNumber(folded.length)})`,
      slot: 8,
      points: data.buckets.map((b) => ({
        at: b.bucket,
        value: folded.reduce((sum, code) => sum + (b.by_modality[code] ?? 0), 0),
      })),
    });
  }
  return series;
}

// ── table sorting (module-level accessors: useSortedRows wants them stable) ──

interface CenterRow extends StudyCenterRow {
  label: string;
  pendingReason: string;
}

type CenterSortKey = 'label' | 'studies' | 'share' | 'patients' | 'series' | 'instances' | 'bytes';

function centerAccessor(row: CenterRow, key: CenterSortKey): unknown {
  return row[key];
}

/** Why a catalogue row is not complete, as a badge tone. */
const ERROR_TONE: Record<string, Tone> = {
  grant: 'critical',
  unreachable: 'critical',
  error: 'critical',
  timeout: 'serious',
  pending: 'neutral',
};

// ── the page ─────────────────────────────────────────────────────────────────

export function StudiesReportPage() {
  const [params, setParams] = useSearchParams();
  /** The bucket-cap refusal, kept on screen after the bucket is put back. */
  const [refusal, setRefusal] = useState('');

  // One URL write per action, carrying every key it moves: a preset change
  // and its bucket reset land together, so there is one request, never one
  // for the old bucket over the new window.
  const navigateTo = useCallback(
    (changes: Partial<Record<UrlKey, string | null>>, replace = false) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === undefined || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace },
      );
    },
    [setParams],
  );
  /** A user's action: it also clears an earlier refusal, now out of date. */
  const go = (changes: Partial<Record<UrlKey, string | null>>, replace = false) => {
    setRefusal('');
    navigateTo(changes, replace);
  };

  const presetKey = readPreset(params.get('range'));
  const preset = PRESETS.find((p) => p.key === presetKey) ?? PRESETS[1]!;
  const isCustom = presetKey === 'custom';
  const slug = (params.get('slug') ?? '').trim();
  const basis = readBasis(params.get('basis'));
  const compare = compareFor(presetKey);

  // "Today" is Baghdad's today, whatever zone this browser is in — the same
  // day the server clamps `to` to.
  const today = baghdadToday();

  const window_ = useMemo(() => {
    if (!isCustom) return { ...calendarRange(presetKey as CalendarPreset, today), invalid: '' };
    const fallback = calendarRange('3m', today);
    const from = params.get('from') ?? fallback.from;
    const to = params.get('to') ?? fallback.to;
    let invalid = '';
    if (!isDay(from) || !isDay(to)) invalid = 'Both dates must be real calendar days.';
    else if (from > to) invalid = 'The start date must not be after the end date.';
    else if (from > today) invalid = 'That range starts in the future; nothing is recorded there yet.';
    else if (from < EXAM_DATE_MIN) invalid = `The report starts at ${EXAM_DATE_MIN}; choose a later start date.`;
    // A typed or hand-edited `to` past today: the server clamps it, so the
    // page does too, or every label would name days the answer stops before.
    return { from, to: !invalid && to > today ? today : to, invalid };
  }, [isCustom, presetKey, params, today]);

  const { from, to, invalid } = window_;
  // An explicit bucket survives only until the window changes (the preset
  // handler deletes it).
  const defaultBucket =
    isCustom && !invalid ? bucketForSpan(daysInclusive(from, to)) : preset.bucket;
  const bucket = readBucket(params.get('bucket')) ?? defaultBucket;
  const bucketInfo = BUCKETS.find((b) => b.key === bucket) ?? BUCKETS[2]!;
  const ready = !invalid;

  // ── requests ───────────────────────────────────────────────────────────────

  const report = useResource<StudyReportResponse>(
    studyReportPath({ from, to, basis, bucket, compare, slug: slug || null }),
    { enabled: ready },
  );
  const centerList = useResource<CenterListResponse>('/api/centers');
  // Refresh is the uploads page's button: the catalogue runs inside the
  // upload rollup, so waking the rollup is what refreshes it.
  const status = useResource<RollupStatusResponse>('/api/reports/uploads/status');
  const me = useResource<MeResponse>('/api/me');
  const canRefresh = me.data?.role === 'operator' || me.data?.role === 'owner';
  const rollupEnabled = status.data?.enabled === true;

  const latest = report.data;
  /** The response is for this center, basis and start (the bucket-refusal
   *  handler below puts back such a response's bucket). */
  const sameScope =
    ready &&
    latest !== null &&
    latest.from_day === from &&
    latest.basis === basis &&
    (latest.slug ?? '') === slug;
  /** The response answers every control on screen (not a stale earlier one).
   *  The server clamps `to` to ITS today: a browser a little ahead of it at
   *  midnight asks up to today and gets yesterday, which is still this answer. */
  const current =
    sameScope &&
    latest.bucket === bucket &&
    latest.compare === compare &&
    (latest.to_day === to || (to === today && latest.to_day < to));
  /** What the page shows: only an answer for the controls on screen. One for
   *  another center, basis, bucket or window (kept by useResource after a
   *  failed or refused request) would put its figures under these labels. */
  const data = current ? latest : null;
  /** Nothing to show yet, but a request for these controls is in flight. */
  const reportLoading = report.loading || (report.refreshing && data === null);

  // A bucket the server refused for its size: show why, and put back the
  // bucket of the last report that loaded for these same controls. With no
  // such report (the scope or basis just changed, and the axis grew with it),
  // fall back to the window's own bucket, which is always under the cap.
  useEffect(() => {
    const error = report.error;
    if (!isBucketRefusal(error)) return;
    setRefusal(error.detail);
    const loaded = sameScope ? latest : null;
    if (!loaded) {
      navigateTo({ bucket: null }, true);
    } else if (loaded.bucket !== bucket) {
      navigateTo({ bucket: loaded.bucket === defaultBucket ? null : loaded.bucket }, true);
    }
    // Reacts to a new error only; the rest is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.error]);

  // ── print ──────────────────────────────────────────────────────────────────

  // Page CSS is bundled globally, so the print rules are scoped to a body
  // class that exists only while this page is mounted. A closed <details>
  // never prints its content, so the catalogue status is opened for printing
  // and closed again after — exactly the ones that were closed.
  useEffect(() => {
    document.body.classList.add('print-studies');
    const opened: HTMLDetailsElement[] = [];
    const beforePrint = () => {
      document.querySelectorAll<HTMLDetailsElement>('details.studies-catalogue').forEach((el) => {
        if (!el.open) {
          el.open = true;
          opened.push(el);
        }
      });
    };
    const afterPrint = () => {
      for (const el of opened.splice(0)) el.open = false;
    };
    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);
    return () => {
      document.body.classList.remove('print-studies');
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, []);

  // ── names ──────────────────────────────────────────────────────────────────

  const centerNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const center of centerList.data?.centers ?? []) {
      if (center.name) names.set(center.slug, center.name);
    }
    for (const row of data?.centers ?? []) {
      if (row.name && !names.has(row.slug)) names.set(row.slug, row.name);
    }
    for (const row of data?.catalogue ?? []) {
      if (row.name && !names.has(row.slug)) names.set(row.slug, row.name);
    }
    return names;
  }, [centerList.data, data]);
  const nameFor = useCallback((key: string) => centerNames.get(key) || key, [centerNames]);

  const scopeTitle = slug ? nameFor(slug) : 'All centers';
  const windowText = `${from} – ${to}, by ${basisNoun(basis)}, Asia/Baghdad`;
  const labelBucket = bucketLabeller(bucket);

  /** A link to the same view, drilled into one center. */
  const drillTo = (target: string) => {
    const next = new URLSearchParams(params);
    next.set('slug', target);
    return `?${next.toString()}`;
  };

  // ── derived ────────────────────────────────────────────────────────────────

  // The bucket cap applies to the axis the server draws — from the first
  // bucket with data (`axis_from`), not from the window's start — so "All
  // time" by day can be fine for a young center. Before the first answer,
  // the window's start is the only axis known.
  const axisFrom = data ? data.axis_from : from;
  const bucketTooMany = (key: UploadBucket) =>
    ready && bucketCount(axisFrom, to, key) > MAX_BUCKETS;
  /** A change of center or basis can move the axis back to `from`: an explicit
   *  bucket too fine for the whole window is dropped with it, not refused. */
  const bucketFitsWindow = !ready || bucketCount(from, to, bucket) <= MAX_BUCKETS;
  const rescope = (changes: Partial<Record<UrlKey, string | null>>) =>
    go(bucketFitsWindow ? changes : { ...changes, bucket: null });

  /** Nothing computed for this scope (or the catalogue cannot be read): an
   *  empty list then means "not known", never "no studies". */
  const notComputed = data !== null && (Boolean(data.unavailable_reason) || data.totals.studies === null);

  const growth = data?.growth ?? null;
  const series = useMemo(() => buildSeries(data), [data]);
  const partialBuckets = useMemo(
    () => new Set((data?.buckets ?? []).filter((b) => b.partial).map((b) => b.bucket)),
    [data],
  );

  const pendingRows = useMemo(() => {
    if (!data || data.pending_slugs.length === 0) return [];
    const bySlug = new Map(data.catalogue.map((c) => [c.slug, c]));
    return data.pending_slugs.map((s) => ({ slug: s, reason: bySlug.get(s)?.pending_reason ?? '' }));
  }, [data]);
  const historyIncomplete =
    data !== null && !data.patients.history_complete && data.patients.history_centers_excluded.length > 0;

  const centerRows = useMemo<CenterRow[]>(() => {
    const reasons = new Map((data?.catalogue ?? []).map((c) => [c.slug, c.pending_reason]));
    return (data?.centers ?? []).map((row) => ({
      ...row,
      label: row.name || nameFor(row.slug),
      pendingReason: reasons.get(row.slug) ?? '',
    }));
  }, [data, nameFor]);
  const centerSort = useSort<CenterSortKey>('studies', 'desc');
  const sortedCenters = useSortedRows(centerRows, centerSort, centerAccessor);

  const lagTotal = (data?.lag ?? []).reduce((sum, row) => sum + row.studies, 0);

  // ── refresh ────────────────────────────────────────────────────────────────

  const [refresh, setRefresh] = useState<{ busy: boolean; message: ReactNode; tone: Tone } | null>(
    null,
  );
  const onRefresh = async () => {
    setRefresh({ busy: true, message: null, tone: 'info' });
    try {
      const answer = await apiPost<RollupRefreshResponse>('/api/reports/uploads/refresh');
      const detail = answer?.detail ? `${answer.detail[0]?.toUpperCase() ?? ''}${answer.detail.slice(1)}.` : '';
      setRefresh({
        busy: false,
        tone: 'good',
        message: `${detail ? `${detail} ` : ''}The rollup reads the catalogue in the background and can take a few minutes; the numbers here change when it finishes — reload this page then.`,
      });
      status.reload();
    } catch (error) {
      setRefresh({ busy: false, tone: 'critical', message: describeError(error) });
    }
  };

  // ── CSV ────────────────────────────────────────────────────────────────────

  const csvButton = (
    build: (r: StudyReportResponse) => CsvTable,
    what: string,
    file: string,
    disabled = false,
  ) => (
    <Button
      kind="ghost"
      size="sm"
      className="no-print"
      renderIcon={DownloadIcon}
      disabled={!data || disabled}
      onClick={() => {
        if (!data) return;
        const [header, rows] = build(data);
        downloadCsv(studyCsvName(data, file), header, rows);
      }}
    >
      CSV<span className="visually-hidden"> of {what}</span>
    </Button>
  );

  // ── render helpers ─────────────────────────────────────────────────────────

  const loadingOr = (what: string, body: () => ReactNode): ReactNode => {
    if (!ready) return <p className="viz__empty">Choose a valid window.</p>;
    if (reportLoading) return <LoadingBlock label={`Loading ${what}…`} />;
    if (!data) return <p className="viz__empty">{UNKNOWN}</p>;
    return body();
  };

  /** A headline tile's growth hint; none at all when there is no growth block. */
  const growthLine = (metric: StudyGrowth | undefined): ReactNode => {
    if (!metric) return null;
    const hint = growthHint(metric, growth);
    if (!hint) return null;
    return (
      <span className="studies-hint" title={hint.title}>
        {hint.text}
      </span>
    );
  };

  const storedNowLine = (): ReactNode => {
    const stored = data?.stored_now;
    if (!stored || stored.bytes === null) return null;
    const stale = stored.centers_stale.length;
    return (
      <span className="studies-hint">
        stored now: {formatBytes(stored.bytes)}
        {stored.as_of ? ` (as of ${stored.as_of})` : ''}
        {stale > 0 ? ` · ${formatNumber(stale)} ${pluralise(stale, 'center')} not reporting` : ''}
      </span>
    );
  };

  /** Up to two hint lines; no hint at all when neither has anything to say. */
  const hints = (first: ReactNode, second: ReactNode = null): ReactNode =>
    first || second ? (
      <>
        {first}
        {second}
      </>
    ) : undefined;

  const totals = data?.totals ?? null;
  const dateRange =
    totals === null
      ? UNKNOWN
      : basis === 'exam'
        ? totals.exam_date_min || totals.exam_date_max
          ? `${totals.exam_date_min ?? UNKNOWN} → ${totals.exam_date_max ?? UNKNOWN}`
          : UNKNOWN
        : totals.first_day || totals.last_day
          ? `${totals.first_day ?? UNKNOWN} → ${totals.last_day ?? UNKNOWN}`
          : UNKNOWN;

  /** The info line: each figure when known and non-zero. */
  const infoItems: string[] = [];
  if (data) {
    const n = (value: number | null) => (value !== null && value > 0 ? value : 0);
    const fallback = n(data.exam_date_fallback);
    const deleted = n(data.deleted_excluded);
    const undated = n(data.undated);
    const skipped = n(data.skipped_studies);
    if (fallback > 0)
      infoItems.push(
        `${formatNumber(fallback)} ${pluralise(fallback, 'study', 'studies')} had no valid exam date and ${fallback === 1 ? 'is' : 'are'} counted on the day ${fallback === 1 ? 'it' : 'they'} arrived`,
      );
    if (deleted > 0)
      infoItems.push(
        `${formatNumber(deleted)} ${pluralise(deleted, 'study', 'studies')} in this window ${deleted === 1 ? 'was' : 'were'} since deleted and ${deleted === 1 ? 'is' : 'are'} not counted`,
      );
    if (undated > 0)
      infoItems.push(
        `${formatNumber(undated)} ${pluralise(undated, 'study has', 'studies have')} no ${basis === 'exam' ? 'exam' : 'received'} date and ${undated === 1 ? 'is' : 'are'} in no window`,
      );
    if (skipped > 0)
      infoItems.push(
        `${formatNumber(skipped)} ${pluralise(skipped, 'study', 'studies')} could not be read and ${skipped === 1 ? 'is' : 'are'} not counted`,
      );
  }

  const otherBasisSentence = (): ReactNode => {
    if (!data || data.other_basis_studies === null) return null;
    const other = data.other_basis_studies;
    const diff = data.totals.studies === null ? null : other - data.totals.studies;
    return (
      <p>
        By {basisNoun(otherBasis(basis))}, this window holds{' '}
        <strong>{formatNumber(other)}</strong> {pluralise(other, 'study', 'studies')}
        {diff !== null ? (
          <>
            {' '}
            ({diff > 0 ? '+' : diff < 0 ? '−' : '±'}
            {formatNumber(Math.abs(diff))} vs {basisNoun(basis)})
          </>
        ) : null}
        .
      </p>
    );
  };

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle={
          <>
            Studies report — {scopeTitle} · {windowText}
            {data ? <> · Generated {formatDateTime(data.generated_at)}</> : null}
          </>
        }
        actions={
          <Button
            kind="tertiary"
            size="sm"
            className="no-print"
            renderIcon={DownloadIcon}
            onClick={() => window.print()}
          >
            Print / PDF
          </Button>
        }
      />

      {/* What a printed page says it is; the subtitle says it on screen. */}
      <div className="print-only studies-print-header">
        <h1>Studies report — {scopeTitle}</h1>
        <p>{windowText}</p>
        <p>Generated {data ? formatDateTime(data.generated_at) : formatDateTime(new Date())}</p>
      </div>

      <div className="bento">
        <ReportTabs className="bento__full" />

        {/* ── filters ────────────────────────────────────────────────────── */}
        <div className="bento__full toolbar">
          <Select
            id="studies-scope"
            labelText="Center"
            value={slug}
            onChange={(event) => rescope({ slug: event.target.value || null })}
          >
            <SelectItem value="" text="All centers (fleet)" />
            {slug && !(centerList.data?.centers ?? []).some((c) => c.slug === slug) ? (
              <SelectItem value={slug} text={`${nameFor(slug)} · ${slug}`} />
            ) : null}
            {(centerList.data?.centers ?? []).map((center) => (
              <SelectItem
                key={center.slug}
                value={center.slug}
                text={center.name ? `${center.name} · ${center.slug}` : center.slug}
              />
            ))}
          </Select>

          <Select
            id="studies-range"
            labelText="Window"
            value={presetKey}
            onChange={(event) => {
              const next = readPreset(event.target.value);
              // A new window resets the bucket to the one that suits it, and
              // Custom starts from the window already on screen.
              go({
                range: next === DEFAULT_PRESET ? null : next,
                bucket: null,
                from: next === 'custom' ? from : null,
                to: next === 'custom' ? to : null,
              });
            }}
          >
            {PRESETS.map((option) => (
              <SelectItem key={option.key} value={option.key} text={option.label} />
            ))}
          </Select>

          {isCustom ? (
            <>
              <TextInput
                id="studies-from"
                labelText="From (Baghdad day)"
                type="date"
                value={from}
                min={EXAM_DATE_MIN}
                max={today}
                onChange={(event) => go({ from: event.target.value, bucket: null }, true)}
              />
              <TextInput
                id="studies-to"
                labelText="To (Baghdad day)"
                type="date"
                value={to}
                min={EXAM_DATE_MIN}
                max={today}
                onChange={(event) => go({ to: event.target.value, bucket: null }, true)}
              />
            </>
          ) : null}

          <div className="field studies-basis">
            <span className="field__label" aria-hidden="true">
              Count by
            </span>
            <SegmentedFilter<StudyBasis>
              legend="Count studies by"
              value={basis}
              options={BASES}
              onChange={(next) => rescope({ basis: next === 'exam' ? null : next })}
            />
            <p className="field__help">
              Exam date = the study&rsquo;s StudyDate; studies without a valid one count on the
              day they arrived.
            </p>
          </div>

          <Select
            id="studies-bucket"
            labelText="Bucket"
            value={bucket}
            onChange={(event) => go({ bucket: event.target.value })}
          >
            {BUCKETS.map((option) => (
              <SelectItem
                key={option.key}
                value={option.key}
                text={bucketTooMany(option.key) ? `${option.label} (over ${MAX_BUCKETS})` : option.label}
                disabled={bucketTooMany(option.key)}
              />
            ))}
          </Select>

          {canRefresh ? (
            <Button
              kind="tertiary"
              size="sm"
              className="push no-print"
              renderIcon={RenewIcon}
              disabled={refresh?.busy === true || !rollupEnabled}
              title={rollupEnabled ? undefined : status.data?.reason || 'The upload rollup is not running.'}
              onClick={() => void onRefresh()}
            >
              {refresh?.busy ? 'Asking…' : 'Refresh now'}
            </Button>
          ) : null}
        </div>

        {slug ? (
          <p className="bento__full">
            Showing <strong>{nameFor(slug)}</strong>
            {centerNames.has(slug) ? <span className="muted"> · {slug}</span> : null}.{' '}
            <Link
              className="no-print"
              to={`?${(() => {
                const next = new URLSearchParams(params);
                next.delete('slug');
                if (!bucketFitsWindow) next.delete('bucket');
                return next.toString();
              })()}`}
            >
              Back to the whole fleet
            </Link>
          </p>
        ) : null}

        {invalid ? (
          <Callout className="bento__full" tone="warning" title="That window cannot be shown">
            {invalid}
          </Callout>
        ) : null}

        {refusal ? (
          <Callout className="bento__full" tone="warning" title="That bucket is too fine for this window" live>
            {refusal}
          </Callout>
        ) : null}

        {refresh && refresh.message ? (
          <Callout
            className="bento__full"
            tone={refresh.tone}
            title={refresh.tone === 'critical' ? 'The refresh was not queued' : 'Refresh queued'}
            live
          >
            {refresh.message}
          </Callout>
        ) : null}

        {report.error && !isBucketRefusal(report.error) ? (
          <div className="bento__full">
            {data ? (
              <StaleBanner error={report.error} onRetry={report.reload} />
            ) : (
              <ErrorBlock what="the studies report" error={report.error} onRetry={report.reload} />
            )}
          </div>
        ) : null}

        {/* ── callouts, in the spec's order ──────────────────────────────── */}
        {data?.unavailable_reason ? (
          <Callout className="bento__full" tone="warning" title="The studies report is not available">
            {data.unavailable_reason}
          </Callout>
        ) : null}

        {data && !data.catalogue_enabled ? (
          <Callout className="bento__full" tone="warning" title="The study catalogue is not running">
            {data.catalogue_reason || 'The study catalogue is switched off.'}
          </Callout>
        ) : null}

        {pendingRows.length > 0 || historyIncomplete ? (
          <Callout
            className="bento__full"
            tone="info"
            title={
              pendingRows.length > 0
                ? `Figures are incomplete for ${formatNumber(pendingRows.length)} ${pluralise(pendingRows.length, 'center')}`
                : 'New and returning patients leave some centers out'
            }
          >
            {pendingRows.length > 0 ? (
              <ul>
                {pendingRows.map((p) => (
                  <li key={p.slug}>
                    {slug ? nameFor(p.slug) : <Link to={drillTo(p.slug)}>{nameFor(p.slug)}</Link>}
                    {p.reason ? <span className="muted"> · {p.reason}</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {historyIncomplete && data?.patients.reason ? <p>{data.patients.reason}</p> : null}
          </Callout>
        ) : null}

        {infoItems.length > 0 ? (
          <p className="bento__full studies-info muted">{infoItems.join(' · ')}.</p>
        ) : null}

        {/* ── totals ─────────────────────────────────────────────────────── */}
        <div className="bento__full">
          <StatGrid>
            <StatTile
              label="Studies"
              value={formatNumber(totals?.studies)}
              hint={hints(growthLine(growth?.studies))}
            />
            <StatTile
              label="Patients"
              value={formatNumber(totals?.patients)}
              hint={hints(growthLine(growth?.patients))}
            />
            <StatTile
              label="Series"
              value={formatNumber(totals?.series)}
              hint={hints(growthLine(growth?.series))}
            />
            <StatTile
              label="Instances"
              value={formatNumber(totals?.instances)}
              hint={hints(growthLine(growth?.instances))}
            />
            <StatTile
              label="Storage"
              value={formatBytes(totals?.bytes)}
              hint={hints(growthLine(growth?.bytes), storedNowLine())}
            />
            <StatTile
              label="Date range"
              value={<span className="studies-date-range">{dateRange}</span>}
              hint={basis === 'exam' ? 'exam dates of the window’s studies' : 'days they were received'}
            />
          </StatGrid>
          <div className="row">
            <span className="push">{csvButton(totalsCsv, 'the totals', 'totals')}</span>
          </div>
        </div>

        {/* ── by type ────────────────────────────────────────────────────── */}
        <Card className="bento__two-thirds">
          <div className="card__body">
            <ChartFrame
              title="Studies by type"
              subtitle="Each study counts once, under its exam label; a study of several modalities is labelled by all of them (CT + PT) or MULTI."
            >
              {loadingOr('the studies by type', () =>
                data!.by_modality.length === 0 ? (
                  <p className="viz__empty">{notComputed ? NOT_COMPUTED : 'No studies in this window.'}</p>
                ) : (
                  <TableScroll label="Studies by type">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th scope="col">Type</th>
                          <th scope="col" className="numeric">Studies</th>
                          <th scope="col" className="numeric">Share</th>
                          <th scope="col" className="numeric">Patients</th>
                          <th scope="col" className="numeric">Series</th>
                          <th scope="col" className="numeric">Instances</th>
                          <th scope="col" className="numeric">Storage</th>
                          <th scope="col" className="numeric">vs previous</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data!.by_modality.map((row) => (
                          <tr key={row.modality || '__none__'}>
                            <td>{modalityDisplay(row.modality)}</td>
                            <td className="numeric">{formatNumber(row.studies)}</td>
                            <td className="numeric">{percent(row.share)}</td>
                            <td className="numeric">{formatNumber(row.patients)}</td>
                            <td className="numeric">{formatNumber(row.series)}</td>
                            <td className="numeric">{formatNumber(row.instances)}</td>
                            <td className="numeric">{formatBytes(row.bytes)}</td>
                            <td className="numeric">{changeCell(row.growth, growth)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                ),
              )}
            </ChartFrame>
            {data && data.attached.length > 0 ? (
              <ul className="studies-footnotes muted">
                {data.attached.map((row) => (
                  <li key={row.modality}>{attachedFootnote(row)}.</li>
                ))}
              </ul>
            ) : null}
            <div className="row">
              <span className="push">
                {csvButton(byTypeCsv, 'the studies by type', 'by-type')}{' '}
                {csvButton(attachedCsv, 'the attached objects', 'attached', !data || data.attached.length === 0)}
              </span>
            </div>
          </div>
        </Card>

        <Card className="bento__third">
          <div className="card__body">
            <ChartFrame title="Share by type" subtitle="Studies in the window, largest first.">
              {loadingOr('the studies by type', () =>
                notComputed ? (
                  <p className="viz__empty">{NOT_COMPUTED}</p>
                ) : (
                <RankedBars
                  rows={data!.by_modality.map((row) => ({
                    key: row.modality || '__none__',
                    label: modalityShort(row.modality),
                    value: row.studies,
                  }))}
                  format={(v) => formatNumber(v)}
                  label="Studies by type"
                />
                ),
              )}
            </ChartFrame>
          </div>
        </Card>

        {/* ── over time ──────────────────────────────────────────────────── */}
        <Card className="bento__full">
          <div className="card__body">
            <ChartFrame
              title={`Studies per ${bucketInfo.noun}`}
              subtitle={`${scopeTitle}, ${from} – ${to}, by ${basisNoun(basis)}. Stacked by type: the parts add up to the total. A faded column is still counting.`}
              series={series}
              unavailable={data?.unavailable_reason || (notComputed ? NOT_COMPUTED : undefined)}
            >
              {loadingOr('the studies over time', () => (
                <ColumnChart
                  series={series}
                  mode="stacked"
                  partial={partialBuckets}
                  format={(v) => formatNumber(Math.round(v))}
                  bucketLabel={labelBucket}
                  label={`Studies per ${bucketInfo.noun}, ${from} to ${to}`}
                />
              ))}
            </ChartFrame>
            {data && data.buckets.length > 0 ? (
              <TableScroll label={`Studies per ${bucketInfo.noun}`}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">{bucketInfo.noun[0]!.toUpperCase() + bucketInfo.noun.slice(1)}</th>
                      <th scope="col" className="numeric">Total</th>
                      {data.modalities.map((code) => (
                        <th scope="col" className="numeric" key={code || '__none__'} title={modalityDisplay(code)}>
                          {modalityShort(code)}
                        </th>
                      ))}
                      <th scope="col" className="numeric">Patients</th>
                      <th scope="col" className="numeric">Instances</th>
                      <th scope="col" className="numeric">Storage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.buckets.map((b) => (
                      <tr key={b.bucket}>
                        <td>
                          {labelBucket(b.bucket)}
                          {b.partial ? <span className="muted"> (partial)</span> : null}
                        </td>
                        <td className="numeric">{formatNumber(b.studies)}</td>
                        {data.modalities.map((code) => (
                          <td className="numeric" key={code || '__none__'}>
                            {formatNumber(b.by_modality[code] ?? 0)}
                          </td>
                        ))}
                        <td className="numeric">{formatNumber(b.patients)}</td>
                        <td className="numeric">{formatNumber(b.instances)}</td>
                        <td className="numeric">{formatBytes(b.bytes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            ) : null}
            <p className="muted studies-note">
              Patients are distinct within each {bucketInfo.noun}, so the rows do not add up to
              the headline.
            </p>
            <div className="row">
              <span className="push">
                {csvButton(perBucketCsv, `the studies per ${bucketInfo.noun}`, `per-${data?.bucket ?? bucket}`, !data || data.buckets.length === 0)}
              </span>
            </div>
          </div>
        </Card>

        {/* ── exam date vs received date ─────────────────────────────────── */}
        <Card className="bento__half">
          <div className="card__body">
            <ChartFrame
              title="Exam date vs received date"
              subtitle="How long after the exam each study reached the center, for studies with a valid exam date."
            >
              {loadingOr('the exam-to-received lag', () => (
                <>
                  {otherBasisSentence()}
                  {notComputed ? (
                    <p className="viz__empty">{NOT_COMPUTED}</p>
                  ) : data!.lag.length === 0 ? (
                    <p className="viz__empty">Nothing catalogued yet.</p>
                  ) : (
                    <TableScroll label="Exam date vs received date">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th scope="col">Received</th>
                            <th scope="col" className="numeric">Studies</th>
                            <th scope="col" className="numeric">Share</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data!.lag.map((row) => (
                            <tr key={row.key}>
                              <td>{row.label}</td>
                              <td className="numeric">{formatNumber(row.studies)}</td>
                              <td className="numeric">
                                {percent(lagTotal > 0 ? row.studies / lagTotal : null)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableScroll>
                  )}
                  <p className="muted studies-note">
                    Studies received long after their exam are usually priors or archive
                    imports; this is why the two bases differ by month.
                  </p>
                </>
              ))}
            </ChartFrame>
            <div className="row">
              <span className="push">
                {csvButton(lagCsv, 'the exam-to-received lag', 'lag', !data || data.lag.length === 0)}
              </span>
            </div>
          </div>
        </Card>

        {/* ── patients ───────────────────────────────────────────────────── */}
        <Card className="bento__half">
          <div className="card__body">
            <ChartFrame title="Patients" subtitle={`${scopeTitle}, ${from} – ${to}.`}>
              <StatGrid>
                <StatTile label="Patients" value={formatNumber(data?.patients.patients)} />
                <StatTile
                  label="New"
                  value={formatNumber(data?.patients.new)}
                  hint="first study seen in this window"
                />
                <StatTile
                  label="Returning"
                  value={formatNumber(data?.patients.returning)}
                  hint="seen before this window"
                />
                <StatTile
                  label="Repeat"
                  value={formatNumber(data?.patients.repeat)}
                  hint={
                    data?.patients.repeat_studies !== null && data?.patients.repeat_studies !== undefined
                      ? `≥ 2 studies · ${formatNumber(data.patients.repeat_studies)} studies in all`
                      : '≥ 2 studies in the window'
                  }
                />
                <StatTile
                  label="Most studies for one patient"
                  value={formatNumber(data?.patients.max_studies)}
                />
              </StatGrid>
              {data && (data.patients.studies_without_patient ?? 0) > 0 ? (
                <p>
                  {formatNumber(data.patients.studies_without_patient)}{' '}
                  {pluralise(data.patients.studies_without_patient ?? 0, 'study has', 'studies have')} no
                  patient ID and {data.patients.studies_without_patient === 1 ? 'is' : 'are'} not
                  counted as {data.patients.studies_without_patient === 1 ? 'a patient' : 'patients'}.
                </p>
              ) : null}
              <p className="muted studies-note">
                Patients are counted per center; one person seen at two centers counts twice in the
                fleet total. Patient names and IDs are never read; patients are counted by a keyed
                pseudonym. Study descriptions are free text copied from the index and shown to
                operators and owners only.
              </p>
              {data?.patients.reason ? <p className="muted studies-note">{data.patients.reason}</p> : null}
            </ChartFrame>
            <div className="row">
              <span className="push">{csvButton(patientsCsv, 'the patient counts', 'patients')}</span>
            </div>
          </div>
        </Card>

        {/* ── per center (fleet scope only) ──────────────────────────────── */}
        {slug ? null : (
          <Card className="bento__full">
            <div className="card__body">
              <ChartFrame
                title="Centers"
                subtitle="Every center in the fleet, even a quiet one. Pick a center to see this report for it alone."
              >
                {loadingOr('the per-center counts', () =>
                  data!.unavailable_reason ? (
                    <p className="viz__empty">{NOT_COMPUTED}</p>
                  ) : centerRows.length === 0 ? (
                    <p className="viz__empty">No centers to report on.</p>
                  ) : (
                    <TableScroll label="Studies by center">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <SortHeader columnKey="label" sort={centerSort}>Center</SortHeader>
                            <SortHeader columnKey="studies" sort={centerSort} numeric>Studies</SortHeader>
                            <SortHeader columnKey="share" sort={centerSort} numeric>Share</SortHeader>
                            <SortHeader columnKey="patients" sort={centerSort} numeric>Patients</SortHeader>
                            <SortHeader columnKey="series" sort={centerSort} numeric>Series</SortHeader>
                            <SortHeader columnKey="instances" sort={centerSort} numeric>Instances</SortHeader>
                            <SortHeader columnKey="bytes" sort={centerSort} numeric>Storage</SortHeader>
                            <th scope="col">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedCenters.map((row) => (
                            <tr key={row.slug}>
                              <td>
                                <Link to={drillTo(row.slug)}>{row.label}</Link>
                                {row.label !== row.slug ? <span className="muted"> · {row.slug}</span> : null}
                              </td>
                              <td className="numeric">{formatNumber(row.studies)}</td>
                              <td className="numeric">{percent(row.share)}</td>
                              <td className="numeric">{formatNumber(row.patients)}</td>
                              <td className="numeric">{formatNumber(row.series)}</td>
                              <td className="numeric">{formatNumber(row.instances)}</td>
                              <td className="numeric">{formatBytes(row.bytes)}</td>
                              <td>
                                {row.pending ? (
                                  <Badge tone="warning" small title={row.pendingReason || undefined}>
                                    Pending
                                  </Badge>
                                ) : (
                                  <Badge tone="good" small>
                                    Complete
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableScroll>
                  ),
                )}
              </ChartFrame>
              <div className="row">
                <span className="push">
                  {csvButton(centersCsv, 'the per-center table', 'centers', centerRows.length === 0)}
                </span>
              </div>
            </div>
          </Card>
        )}

        {/* ── descriptions (operators and owners only, server-side) ──────── */}
        {data && !data.descriptions_visible ? (
          <p className="bento__full muted">Study descriptions are shown to operators and owners only.</p>
        ) : data ? (
          <Card className="bento__full">
            <div className="card__body">
              <ChartFrame
                title="Top study descriptions"
                subtitle="Grouped ignoring case, shown in their most common spelling. Share is of every study in the window."
              >
                {(data.top_descriptions ?? []).length === 0 ? (
                  <p className="viz__empty">
                    {notComputed ? NOT_COMPUTED : 'No study descriptions in this window.'}
                  </p>
                ) : (
                  <TableScroll label="Top study descriptions">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th scope="col" className="numeric">Rank</th>
                          <th scope="col">Description</th>
                          <th scope="col" className="numeric">Studies</th>
                          <th scope="col" className="numeric">Share</th>
                          <th scope="col">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.top_descriptions ?? []).map((row, index) => (
                          <tr key={`${index}-${row.description}`}>
                            <td className="numeric">{index + 1}</td>
                            <td>{row.description}</td>
                            <td className="numeric">{formatNumber(row.studies)}</td>
                            <td className="numeric">{percent(row.share)}</td>
                            <td title={modalityDisplay(row.modality)}>{modalityShort(row.modality)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                )}
                {data.studies_without_description !== null ? (
                  <p className="muted studies-note">
                    (no description): {formatNumber(data.studies_without_description)}{' '}
                    {pluralise(data.studies_without_description, 'study', 'studies')}
                  </p>
                ) : null}
              </ChartFrame>
              <div className="row">
                <span className="push">
                  {csvButton(
                    descriptionsCsv,
                    'the top study descriptions',
                    'descriptions',
                    (data.top_descriptions ?? []).length === 0,
                  )}
                </span>
              </div>
            </div>
          </Card>
        ) : null}

        {/* ── catalogue status ───────────────────────────────────────────── */}
        <Card className="bento__full">
          <div className="card__body">
            <details className="studies-catalogue">
              <summary className="studies-catalogue__summary">
                Catalogue status
                {data && !data.unavailable_reason ? (
                  <span className="muted">
                    {' '}
                    · {data.complete ? 'every center complete' : `${formatNumber(data.pending_slugs.length)} pending`}
                  </span>
                ) : null}
              </summary>
              {loadingOr('the catalogue status', () =>
                data!.catalogue.length === 0 ? (
                  <p className="viz__empty">No catalogue state to show.</p>
                ) : (
                  <TableScroll label="Catalogue status">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th scope="col">Center</th>
                          <th scope="col" className="numeric">Catalogued / in index</th>
                          <th scope="col">Backfill</th>
                          <th scope="col" className="numeric">Not counted</th>
                          <th scope="col" className="numeric">Read timeouts</th>
                          <th scope="col" className="numeric">Held deletions</th>
                          <th scope="col">Last success</th>
                          <th scope="col">Last reconcile</th>
                          <th scope="col">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data!.catalogue.map((row) => (
                          <tr key={row.slug}>
                            <td>
                              {row.name || nameFor(row.slug)}
                              {row.database_name ? <span className="muted"> · {row.database_name}</span> : null}
                            </td>
                            <td className="numeric">
                              {formatNumber(row.catalogued)} / {formatNumber(row.index_studies)}
                            </td>
                            <td>
                              <Badge tone={row.backfill_complete ? 'good' : 'info'} small>
                                {row.backfill_complete ? 'Complete' : 'Backfilling'}
                              </Badge>
                            </td>
                            <td className="numeric">{formatNumber(row.skipped_studies)}</td>
                            <td className="numeric">{formatNumber(row.read_timeouts)}</td>
                            <td className="numeric">{formatNumber(row.held_deletions)}</td>
                            <td>
                              {row.last_success_at ? (
                                <time dateTime={row.last_success_at} title={formatDateTime(row.last_success_at)}>
                                  {formatRelative(row.last_success_at)}
                                </time>
                              ) : (
                                UNKNOWN
                              )}
                              {row.stale && row.last_success_at ? <span className="muted"> · stale</span> : null}
                            </td>
                            <td>{row.last_reconciled_day ?? UNKNOWN}</td>
                            <td>
                              {row.last_error_kind ? (
                                <>
                                  <Badge tone={ERROR_TONE[row.last_error_kind] ?? 'critical'} small>
                                    {row.last_error_kind}
                                  </Badge>{' '}
                                </>
                              ) : null}
                              {row.last_error ? (
                                <span className="muted">{row.last_error}</span>
                              ) : row.last_error_kind ? null : (
                                UNKNOWN
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                ),
              )}
            </details>
            <div className="row">
              <span className="push">
                {csvButton(catalogueCsv, 'the catalogue status', 'catalogue', !data || data.catalogue.length === 0)}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}
